import Image from "next/image";

import type { PageTestSource, RunPhase, TestRun } from "@/lib/qa-types";
import { phaseLabel } from "@/lib/demo-data";

export function RunsView({
    phase,
    run,
    source,
    onStart,
    onOpenOverview,
}: {
    phase: RunPhase;
    run: TestRun | null;
    source: PageTestSource | null;
    onStart: () => void;
    onOpenOverview: () => void;
}) {
    const active = phase !== "idle";
    return (
        <section className="workspace-view">
            <div className="view-heading">
                <div>
                    <p className="eyebrow">EXECUTION CONTROL</p>
                    <h1>任务执行与自愈追踪</h1>
                    <p>每个步骤的定位候选、分数、租约和证据均可回放。</p>
                </div>
                <div className="view-heading-actions">
                    {phase === "passed" && (
                        <button className="button secondary" onClick={onOpenOverview}>
                            查看质量总览
                        </button>
                    )}
                    <button className="button primary" onClick={onStart}>
                        {run ? "重新执行当前场景" : "新建演示执行"}
                    </button>
                </div>
            </div>
            {source?.exploration && <RemoteBrowserExecution phase={phase} source={source} />}
            <div className="run-detail-grid">
                <div className="card execution-card">
                    <div className="execution-title">
                        <div>
                            <span className={`pulse-dot ${active ? "active" : ""}`} />
                            <div>
                                <small>{run?.run_id ?? "RUN-0818-DEMO"}</small>
                                <h2>{source ? `${source.title} · 场景回归` : "商城结算核心回归"}</h2>
                            </div>
                        </div>
                        <span className={`large-status ${phase}`}>{phaseLabel(phase)}</span>
                    </div>
                    <div className="execution-meta">
                        <span>
                            环境 <strong>UAT</strong>
                        </span>
                        <span>
                            优先级 <strong>{run?.priority ?? "P0"}</strong>
                        </span>
                        <span>
                            Agent <strong>{run?.agent_id ?? "agent-sh-01"}</strong>
                        </span>
                        <span>
                            租约 <strong>05:00</strong>
                        </span>
                    </div>
                    <div className="timeline">
                        <TimelineItem
                            done={active}
                            title="测试场景进入优先级队列"
                            detail={`${run?.case_ids.length ?? 3} 条场景 · 来源 ${source?.target_url ?? "演示需求"}`}
                        />
                        <TimelineItem
                            done={["running", "healing", "passed"].includes(phase)}
                            title="Agent 获取租约并启动容器"
                            detail="镜像 playwright:1.62 · 非特权模式"
                        />
                        <TimelineItem
                            done={["healing", "passed"].includes(phase)}
                            title="步骤 S3 原定位失效"
                            detail="button#submit-order 在当前 DOM 中不存在"
                            warning
                        />
                        <TimelineItem
                            done={phase === "passed"}
                            title="联合候选匹配完成"
                            detail="视觉 0.93 · 语义 0.96 · 安全阈值 0.90"
                            healed
                        />
                    </div>
                </div>
                <aside className="card evidence-card">
                    <p className="section-kicker">证据包</p>
                    <h2>失败可复现</h2>
                    {(run?.artifacts.length
                        ? run.artifacts.map((artifact) => artifact.name)
                        : ["执行录屏.webm", "DOM 快照.html", "网络日志.har", "最终截图.png"]
                    ).map((name, index) => (
                        <div className="evidence-item" key={name}>
                            <span>{["VID", "DOM", "HAR", "PNG"][index]}</span>
                            <div>
                                <strong>{name}</strong>
                                <small>已脱敏 · 待归档</small>
                            </div>
                        </div>
                    ))}
                    <div className="jira-box">
                        <span>JIRA</span>
                        <div>
                            <strong>一键创建缺陷</strong>
                            <p>自动附带环境、复现步骤与证据链接</p>
                        </div>
                    </div>
                </aside>
            </div>
        </section>
    );
}

function RemoteBrowserExecution({ phase, source }: { phase: RunPhase; source: PageTestSource }) {
    const exploration = source.exploration!;
    const target = exploration.elements[0];
    const phaseContent = {
        idle: ["等待调度", "远程浏览器等待 Agent 获取任务"],
        queued: ["正在分配浏览器", "创建隔离执行环境并加载页面快照"],
        running: ["正在远程执行", target ? `定位 #${target.index} ${target.name} 并执行测试动作` : "按场景步骤执行页面操作"],
        healing: ["正在视觉自愈", "DOM 定位发生变化，正在比对视觉与语义候选"],
        passed: ["远程执行完成", "浏览器证据已归档，所有场景执行通过"],
    }[phase];
    const completedSteps = { idle: 0, queued: 1, running: 2, healing: 3, passed: 4 }[phase];
    const logs = [
        `[browser] connected · session ${exploration.session_id}`,
        `[navigate] ${exploration.current_url}`,
        target ? `[locator] #${target.index} ${target.role} "${target.name}"` : "[locator] building semantic candidates",
        phase === "healing"
            ? "[healing] visual 0.93 · semantic 0.96 · accepted"
            : phase === "passed"
              ? "[result] PASSED · artifacts archived"
              : "[agent] executing scenario steps_",
    ];

    return (
        <section className={`card remote-browser-execution phase-${phase}`} aria-label="远程浏览器执行过程">
            <header className="remote-browser-head">
                <div>
                    <p className="section-kicker">REMOTE BROWSER · LIVE</p>
                    <h2>远程浏览器执行过程</h2>
                </div>
                <div className="remote-browser-state">
                    <i />
                    <span>
                        <strong>{phaseContent[0]}</strong>
                        <small>{phaseContent[1]}</small>
                    </span>
                </div>
            </header>
            <div className="remote-browser-grid">
                <div className="remote-browser-viewport">
                    <div className="remote-browser-toolbar">
                        <span>←</span>
                        <span>→</span>
                        <span>↻</span>
                        <strong>{exploration.current_url}</strong>
                        <small>CHROME · 1280×720</small>
                    </div>
                    <div className="remote-browser-screen">
                        <Image
                            alt={`${source.title} 的远程浏览器执行画面`}
                            height={720}
                            src={exploration.screenshot_data_url}
                            unoptimized
                            width={1280}
                        />
                        {phase !== "idle" && phase !== "passed" && (
                            <div className="remote-cursor-pulse">
                                <span />
                                <small>{phase === "healing" ? "重新定位" : phase === "running" ? "执行动作" : "连接页面"}</small>
                            </div>
                        )}
                    </div>
                </div>
                <aside className="remote-execution-console">
                    <div className="remote-step-list">
                        {["分配远程浏览器", "打开目标页面", "执行场景步骤", "归档证据与结果"].map((label, index) => (
                            <div className={index < completedSteps ? "done" : index === completedSteps ? "current" : ""} key={label}>
                                <b>{index < completedSteps ? "✓" : String(index + 1).padStart(2, "0")}</b>
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                    <div className="remote-console-log">
                        <strong>AGENT CONSOLE</strong>
                        {logs.map((log) => (
                            <code key={log}>{log}</code>
                        ))}
                    </div>
                </aside>
            </div>
        </section>
    );
}

function TimelineItem({
    done,
    title,
    detail,
    warning,
    healed,
}: {
    done: boolean;
    title: string;
    detail: string;
    warning?: boolean;
    healed?: boolean;
}) {
    return (
        <div className={`timeline-item ${done ? "done" : ""} ${warning ? "warning" : ""} ${healed ? "healed" : ""}`}>
            <span>{done ? "✓" : ""}</span>
            <div>
                <strong>{title}</strong>
                <p>{detail}</p>
            </div>
        </div>
    );
}
